Retorna o detalhe público de uma campanha específica da organização autenticada.

Use este endpoint quando a integração já possui o `id` vindo da listagem e precisa consultar a configuração completa da campanha.

<ApiCodeExamples exampleId="campaignDetail" />

<ApiResponseExamples exampleId="campaignDetail" />

## Path parameter

- `id` (`uuid`, obrigatório): identificador da campanha.

## Resposta `200`

- `id` (`uuid`): identificador da campanha.
- `canonical_url` (`string`): link canônico da campanha no app Contact-Tel.
- `created_by_id` (`uuid|null`): identificador do usuário que criou a campanha.
- `created_by_name` (`string|null`): nome exibível do usuário que criou a campanha.
- `name` (`string`): nome da campanha.
- `description` (`string`): descrição livre da campanha.
- `status` (`string`): status operacional persistido.
  Valores possíveis:
  `draft`, `processing`, `ready`, `active`, `paused`, `completed`, `cancelled`
- `status_reason` (`string|null`): código público e sanitizado para explicar estados especiais.
  Valores possíveis:
  `import_failed`, `config_error`, `paused`, `null`
- `campaign_type` (`string`): modalidade operacional.
  Valores possíveis:
  `ai`, `reverse_ivr`
- `ai_agent_provider_id` (`uuid|null`): identificador do provedor de agente/telefonia usado pela campanha.
- `ai_agent_provider_name` (`string|null`): nome exibível do provedor usado pela campanha.
- `ai_agent_provider_type` (`string|null`): tipo do provedor usado pela campanha.
- `ai_agent_id` (`uuid|null`): identificador do agente vinculado à campanha.
- `ai_agent_name` (`string|null`): nome exibível do agente vinculado.
- `sip_trunk_id` (`uuid|null`): identificador do SIP trunk, quando a campanha usa esse recurso.
- `sip_trunk_name` (`string|null`): nome exibível do SIP trunk.
- `max_concurrent_calls` (`integer`): limite de concorrência da campanha.
- `max_retries` (`integer`): máximo de tentativas por contato.
- `retry_delay_min_seconds` (`integer`): menor intervalo permitido entre tentativas.
- `retry_delay_max_seconds` (`integer`): maior intervalo permitido entre tentativas.
- `schedule` (`array<object>`): agenda operacional configurada para a campanha.
- `tags` (`array<string>`): tags livres associadas à campanha.
- `transfer_target_type` (`string|null`): tipo do alvo de transferência configurado.
  Valores possíveis:
  `user`, `group`, `null`
- `transfer_target_id` (`uuid|null`): identificador público do usuário/grupo alvo de transferência.
- `transfer_target_name` (`string|null`): nome exibível do alvo de transferência.
- `transfer_target_extension` (`string|null`): ramal do alvo quando o alvo é um usuário com ramal ativo.
- `ivr_dtmf_map` (`object`): mapa DTMF configurado para campanhas Reverse IVR.
- `import_status` (`string`): status do pipeline de importação.
  Valores possíveis:
  `idle`, `processing`, `completed`, `failed`
- `import_progress` (`number`): progresso da importação em percentual.
- `import_processed_rows` (`integer`): linhas já processadas na importação.
- `import_total_rows` (`integer`): total de linhas esperado na importação.
- `import_started_at` (`datetime|null`): instante em que a importação começou.
- `import_completed_at` (`datetime|null`): instante em que a importação terminou.
- `total_contacts` (`integer`): total de contatos da campanha.
- `total_phones` (`integer`): total de telefones da campanha.
- `pending_contacts` (`integer`): contatos ainda pendentes.
- `in_progress_contacts` (`integer`): contatos em execução.
- `reached_contacts` (`integer`): contatos já alcançados.
- `converted_contacts` (`integer`): contatos convertidos.
- `optout_contacts` (`integer`): contatos marcados como opt-out.
- `mismatch_contacts` (`integer`): contatos marcados como mismatch.
- `exhausted_contacts` (`integer`): contatos esgotados.
- `no_valid_phones_contacts` (`integer`): contatos sem telefone válido.
- `skipped_contacts` (`integer`): contatos ignorados/cancelados pelo fluxo operacional.
- `total_calls` (`integer`): total de chamadas vinculadas.
- `connected_calls` (`integer`): chamadas conectadas.
- `conversion_eligible_calls` (`integer`): chamadas elegíveis para cálculo de conversão.
- `failed_calls` (`integer`): chamadas com falha operacional.
- `converted_calls` (`integer`): chamadas convertidas.
- `ai_billed_seconds` (`integer`): segundos faturáveis líquidos de IA na campanha.
- `ai_gross_billed_seconds` (`integer`): segundos faturáveis brutos de IA na campanha.
- `ai_refunded_seconds` (`integer`): segundos de IA estornados/refundados na campanha.
- `telephony_billed_seconds` (`integer`): segundos faturáveis líquidos de telefonia na campanha.
- `telephony_gross_billed_seconds` (`integer`): segundos faturáveis brutos de telefonia na campanha.
- `telephony_refunded_seconds` (`integer`): segundos de telefonia estornados/refundados na campanha.
- `conversion_rate` (`number`): taxa de conversão calculada sobre chamadas elegíveis.
- `progress_percentage` (`number`): progresso calculado em percentual.
  Regra:
  faixa de `0` a `100`
- `created_at` (`datetime`): instante de criação.
- `updated_at` (`datetime`): instante da última atualização.
- `started_at` (`datetime|null`): instante de início operacional.
- `completed_at` (`datetime|null`): instante de conclusão operacional.

### Referência de opções

#### `status`

- `draft`: campanha em rascunho, ainda sem execução liberada.
- `processing`: campanha preparando/importando contatos.
- `ready`: campanha pronta para iniciar.
- `active`: campanha em execução.
- `paused`: campanha pausada.
- `completed`: campanha concluída.
- `cancelled`: campanha cancelada.

#### `status_reason`

- `import_failed`: a campanha está bloqueada por falha na importação.
- `config_error`: a campanha está pausada por erro de configuração.
- `paused`: pausa operacional sem erro público específico.
- `null`: não há motivo público adicional para o status atual.

#### `campaign_type`

- `ai`: campanha conduzida por agente de IA.
- `reverse_ivr`: campanha de URA reversa com áudio e DTMF.

#### `ai_agent_provider_type`

- `cloud`: provedor cloud de agente/telefonia.
- `asterisk`: provedor Asterisk/SIP local.
- `sip`: provedor SIP externo.

#### `transfer_target_type`

- `user`: transferência para usuário/operador específico.
- `group`: transferência para grupo de atendimento.
- `null`: campanha sem alvo de transferência configurado.

#### `import_status`

- `idle`: nenhum processamento de importação em andamento.
- `processing`: importação de contatos em andamento.
- `completed`: importação concluída.
- `failed`: importação falhou.

## Erros esperados

- `401 Unauthorized`: token inválido, expirado, revogado, ausente ou bloqueado por IP.
- `403 Forbidden`: a credencial autenticou, mas não possui `campaigns.read`.
- `404 Not Found`: a campanha não existe ou não pertence à organização autenticada.

## Quando usar este endpoint

- consultar uma campanha específica a partir do `id` vindo da listagem;
- sincronizar estado/configuração pública em sistemas externos;
- validar detalhes operacionais de uma campanha sem abrir a interface do produto.
